import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  clearLoginError,
  clearMfaPending,
  completeMfaLogin,
  loginUser,
  selectLoginError,
  selectLoginLoading,
  selectMfaPending,
} from '../authSlice';
import { useAppDispatch } from '../../../store/hooks';
import { Credentials } from '../types';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

export const LoginForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Credentials>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const rawFrom = location.state?.from?.pathname || '/';
  const from = typeof rawFrom === 'string' && rawFrom.startsWith('/') && !rawFrom.startsWith('//') ? rawFrom : '/';
  const isLoading = useSelector(selectLoginLoading);
  const loginError = useSelector(selectLoginError);
  const mfaPending = useSelector(selectMfaPending);
  const [totpCode, setTotpCode] = useState('');

  useEffect(() => {
    return () => {
      dispatch(clearLoginError());
    };
  }, [dispatch]);

  const onSubmit = async (credentials: Credentials) => {
    const result = await dispatch(loginUser(credentials));
    if (loginUser.fulfilled.match(result) && !result.payload.mfaRequired) {
      navigate(from, { replace: true });
    }
  };

  const onSubmitMfa = async () => {
    if (!mfaPending || totpCode.length < 6) return;
    const result = await dispatch(
      completeMfaLogin({ mfaToken: mfaPending.mfaToken, totpCode: totpCode.trim() }),
    );
    if (completeMfaLogin.fulfilled.match(result)) {
      navigate(from, { replace: true });
    }
  };

  const handleBackToCredentials = () => {
    dispatch(clearMfaPending());
    dispatch(clearLoginError());
    setTotpCode('');
  };

  if (mfaPending) {
    return (
      <div className='space-y-4 w-full max-w-sm'>
        <p className='text-sm text-muted-foreground'>
          Ingresá el código de tu app autenticadora para <strong>{mfaPending.user.email}</strong>.
        </p>
        <Input
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          placeholder='Código de 6 dígitos'
          value={totpCode}
          maxLength={8}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
        />
        {loginError && <p className='text-sm text-red-500'>{loginError}</p>}
        <Button type='button' className='w-full' disabled={isLoading || totpCode.length < 6} onClick={() => void onSubmitMfa()}>
          {isLoading ? 'Verificando...' : 'Verificar código'}
        </Button>
        <button type='button' className='w-full text-sm text-primary hover:underline' onClick={handleBackToCredentials}>
          Volver al login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 w-full max-w-sm'>
      <div className='space-y-2'>
        <Input
          type='email'
          placeholder='Email'
          autoComplete='username'
          {...register('email', {
            required: 'El email es requerido',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Email inválido',
            },
          })}
        />
        {errors.email && <p className='text-sm text-red-500'>{errors.email.message}</p>}
      </div>

      <div className='space-y-2'>
        <Input
          type='password'
          placeholder='Contraseña'
          autoComplete='current-password'
          {...register('password', {
            required: 'La contraseña es requerida',
            minLength: {
              value: 6,
              message: 'La contraseña debe tener al menos 6 caracteres',
            },
          })}
        />
        {errors.password && <p className='text-sm text-red-500'>{errors.password.message}</p>}
      </div>

      {loginError && <p className='text-sm text-red-500'>{loginError}</p>}

      <Button type='submit' className='w-full' disabled={isLoading}>
        {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
      </Button>

      <p className='text-center text-sm'>
        <Link to='/forgot-password' className='text-primary hover:underline'>
          Olvidé mi contraseña
        </Link>
      </p>
    </form>
  );
};
